// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { THUserSelector } from '@Molecules/Inputs/THUserSelector/THUserSelector';
import { useCurrentUser } from '@Queries/userQueries';
import { useEffect, useMemo, useState } from 'react';
import { UserOutput } from 'thehive-sdk';
import { Form } from 'antd';

import './AssigneeForm.scss';

interface WithAssignee {
    assignee?: string | null;
}

interface AssigneeFormProps<T extends WithAssignee> {
    assignableUsers: UserOutput[];
    currentAssignee: string;
    onFinish: (newValues?: Partial<T>) => void;
    disabled?: boolean;
}

export const AssigneeForm = <T extends WithAssignee>({
    assignableUsers,
    currentAssignee,
    onFinish,
    disabled = false,
}: AssigneeFormProps<T>) => {
    const locale = useLocalMessage();
    const [confirmIsOpen, setConfirmIsOpen] = useState<boolean>(false);
    const { data: currentUser } = useCurrentUser();
    const { login, profile } = currentUser || {};
    const [form] = Form.useForm();

    /**
     * [TH5-1765] Assign to me button is not visible when:
     * - Form is disabled
     * - user has profile 'read-only'
     * - current assignee is current user
     * - change confirmation is open
     */
    const canDisplayAssignToMeButton = useMemo(
        () => !disabled && profile !== 'read-only' && login !== currentAssignee && !confirmIsOpen,
        [confirmIsOpen, currentAssignee, login, profile, disabled],
    );

    const label = useMemo(
        () => (
            <span className="AssigneeForm AssigneeForm__Label">
                <span>{locale('common.assignee')}</span>
                {canDisplayAssignToMeButton && (
                    <THButton
                        type="link"
                        onClick={() => {
                            if (login) {
                                {
                                    onFinish({ assignee: login } as Partial<T>);
                                    form.setFieldsValue({
                                        assignee: login,
                                    });
                                }
                            }
                        }}
                    >
                        {locale('common.assigntome')}
                    </THButton>
                )}
            </span>
        ),
        [canDisplayAssignToMeButton, locale, login, onFinish, form],
    );

    useEffect(() => {
        form.resetFields();
    }, [currentAssignee, form]);

    return (
        <THEditable<Partial<T>>
            form={form}
            name="assignee"
            label={label}
            initialValues={{ assignee: currentAssignee } as T}
            onFinish={(values) =>
                onFinish({ ...values, assignee: 'assignee' in values && !values.assignee ? null : values.assignee })
            }
            onConfirmIsOpen={(isOpen) => {
                setConfirmIsOpen(isOpen);
            }}
            className="AssigneeForm"
        >
            <THUserSelector
                allowClear={false}
                placeholder={locale('general.unassigned')}
                users={assignableUsers}
                disabled={disabled}
            />
        </THEditable>
    );
};
